import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy, Email, PersonAdd } from '@mui/icons-material';
import liveStudioApi, { getLiveStudioError } from './liveStudioApi';

const inviteUrlFrom = (payload) => {
  const invite = payload?.invite || payload || {};
  const provided = invite.joinUrl || invite.inviteUrl || invite.url;
  if (provided) {
    try {
      return new URL(provided, window.location.origin).toString();
    } catch {
      return provided;
    }
  }
  return invite.token
    ? new URL(`/live/join/${encodeURIComponent(invite.token)}`, window.location.origin).toString()
    : '';
};

export default function InvitationDialog({
  open,
  roomId,
  invites = [],
  onChanged,
  onClose,
  api = liveStudioApi,
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setDisplayName('');
      setInviteUrl('');
      setError('');
      setStatus('');
    }
  }, [open]);

  const createInvite = async () => {
    if (!displayName.trim()) {
      setError('Enter the guest’s display name.');
      return;
    }
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const payload = await api.createInvite(roomId, {
        email: email.trim(),
        displayName: displayName.trim(),
      });
      const nextUrl = inviteUrlFrom(payload);
      setInviteUrl(nextUrl);
      if (email.trim() && payload?.delivery?.sent === false) {
        setStatus('Invitation link created.');
        setError(`Email was not sent: ${payload.delivery.reason || 'SMTP delivery failed'}. Copy the link instead.`);
      } else {
        setStatus(email.trim() ? 'Invitation email sent.' : 'Invitation link created.');
      }
      onChanged?.();
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not create invitation.'));
    } finally {
      setLoading(false);
    }
  };

  const revokeInvite = async (inviteId) => {
    setError('');
    try {
      await api.revokeInvite(roomId, inviteId);
      setStatus('Invitation revoked.');
      onChanged?.();
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not revoke invitation.'));
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setStatus('Invitation link copied.');
    } catch {
      setError('Clipboard access was blocked. Select and copy the link manually.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonAdd color="info" />
        Invite a studio guest
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {status && <Alert severity="success">{status}</Alert>}
          <TextField
            autoFocus
            required
            label="Display name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            inputProps={{ maxLength: 80 }}
          />
          <TextField
            type="email"
            label="Email (optional)"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            helperText="Leave blank to create a link without sending email."
            inputProps={{ maxLength: 254 }}
          />
          {inviteUrl && (
            <TextField
              label="Guest link"
              value={inviteUrl}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
          {invites.length > 0 && (
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <Typography variant="subtitle2">Contributors</Typography>
              {invites.map((invite) => (
                <Stack
                  key={invite.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {invite.display_name || invite.displayName || invite.email || 'Guest'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {invite.email || 'Link invitation'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" label={invite.status} />
                    {!['revoked', 'expired'].includes(invite.status) && (
                      <Button size="small" color="error" onClick={() => revokeInvite(invite.id)}>
                        Revoke
                      </Button>
                    )}
                  </Stack>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {inviteUrl && (
          <Button variant="outlined" startIcon={<ContentCopy />} onClick={copyInvite}>
            Copy link
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Email />}
          disabled={loading || !roomId}
          onClick={createInvite}
        >
          {loading ? 'Creating…' : email.trim() ? 'Send invite' : 'Create link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
